import { useEffect, useRef, useState } from "react";
import { Column } from "../../../../core/components/Column";
import { Row } from "../../../../core/components/Row";
import { TextComponent } from "../../../../core/components/TextComponent";
import { Button } from "../../../../core/components/Button";
import animationLottery from "../../../../core/assets/lottery.json";
import { useLottie } from "lottie-react";
import { handleLottery } from "../../../../core/constants/utils";
import { triggerConfetti } from "../../../../core/constants/function";
import { RenderInput } from "../components/RenderInput";
import { RouletteIcon } from "../../../../core/icons/Roulette";
import { TrashIcon } from "../../../../core/icons/Trash";
import { LinkIcon } from "../../../../core/icons/Link";
import { RenderTop } from "../components/RenderTop";
import { Ref } from "../../../../core/components/Ref";
import { useInputHandler } from "../../../../core/hooks/useInputHandler";
import { RenderTable } from "../components/RenderTabe";

export const HomeScreen: React.FC = () => {
	const [result, setResult] = useState<{ participants: string[]; prize: string[] }[]>([]);
	const [isAnimating, setIsAnimating] = useState<boolean>(false);
	const [isOnly, setIsOnly] = useState(true);
	const lottieRef = useRef<HTMLDivElement>(null);
	const resultRef = useRef<HTMLDivElement>(null);
	const options = {
		animationData: animationLottery,
		loop: false,
	};
	const { View, play, stop, setSpeed } = useLottie(options);
	const animationDuration = 4024;
	const desiredDuration = 2000;

	useEffect(() => {
		const speedMultiplier = animationDuration / desiredDuration;
		setSpeed(speedMultiplier);
	}, [setSpeed]);

	const handleClear = () => {
		setListParticipant([]);
		setListPrizes([]);
		setResult([]);
		setValueInputParticipant("");
		setValueInputPrize("");
		setInputError((prevState) => ({ ...prevState, participant: false, prize: false }));
	};

	const handleLotteryResult = () => {
		if (listParticipant.length === 0 || listPrizes.length === 0) {
			setInputError((prevState) => ({ ...prevState, participant: listParticipant.length === 0, prize: listPrizes.length === 0 }));
			return;
		}
		setIsAnimating(true);
		setTimeout(() => {
			if (lottieRef.current) lottieRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
		}, 100);
		stop();
		play();
		setResult([]);
		if (listParticipant.length === 0 || listPrizes.length === 0) return;
		setTimeout(() => {
			handleLottery(listParticipant, listPrizes, setResult, isOnly);
			setIsAnimating(false);
			triggerConfetti(2);
			setTimeout(() => {
				if (resultRef.current) resultRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
			}, 500);
		}, desiredDuration);
	};

	const handleCheckboxEquitativeChange = (newSelectedValues: string[]) => {
		setIsOnly(newSelectedValues.length > 0 ? true : false);
	};

	const {
		valueInputParticipant,
		setValueInputParticipant,
		valueInputPrize,
		setValueInputPrize,
		listParticipant,
		setListParticipant,
		listPrizes,
		setListPrizes,
		inputError,
		setInputError,
		handleInputChange,
		handleKeyDown,
	} = useInputHandler();
	useEffect(() => {
		if (listParticipant.length > 0) setInputError((prevState) => ({ ...prevState, participant: false }));
		if (listPrizes.length > 0) setInputError((prevState) => ({ ...prevState, prize: false }));
	}, [listParticipant, listPrizes, setInputError]);

	return (
		<Column
			crossAxisAlignment="center"
			className="py-5 px-5 gap-3 md:gap-4"
		>
			<RenderTop
				handleCheckboxEquitativeChange={handleCheckboxEquitativeChange}
				isOnly={isOnly}
			/>
			<RenderInput
				id="participant"
				label="Escribe el nombre del participante"
				valueInput={valueInputParticipant}
				onChange={(e) => handleInputChange(e, "participant")}
				onKeyDown={(e) => handleKeyDown(e, "participant")}
				list={listParticipant}
				setList={setListParticipant}
				error={inputError.participant}
				isReadonly={isAnimating}
			/>
			<RenderInput
				id="prize"
				label="¿Qué premios deseas sortear?"
				valueInput={valueInputPrize}
				onChange={(e) => handleInputChange(e, "prize")}
				onKeyDown={(e) => handleKeyDown(e, "prize")}
				list={listPrizes}
				setList={setListPrizes}
				error={inputError.prize}
				isReadonly={isAnimating}
			/>
			<Row
				className="gap-2 font-bold"
				flexible
			>
				<Button
					className="w-1/2"
					color="yellow"
					onClick={handleClear}
					isDisabled={isAnimating}
				>
					<Row
						className="gap-1"
						crossAxisAlignment="center"
						mainAxisAlignment="center"
					>
						<TrashIcon className="w-5 h-5" />
						<span>Limpiar</span>
					</Row>
				</Button>
				<Button
					className="w-1/2"
					color="orange"
					onClick={handleLotteryResult}
					isDisabled={isAnimating}
					secondary
				>
					<Row
						className="gap-1"
						crossAxisAlignment="center"
						mainAxisAlignment="center"
					>
						<RouletteIcon className="w-5 h-5" />
						<span>Sortear</span>
					</Row>
				</Button>
			</Row>
			<Column
				ref={lottieRef}
				className={`w-80 lg:w-96 ${isAnimating ? "animate-scaleIn" : "hidden"}`}
			>
				{View}
			</Column>
			<RenderTable
				result={result}
				ref={resultRef}
			/>
			<Column
				className="border gap-2 sm:w-2/3 text-center border-yellow-500 p-4 sm:p-6 rounded-md bg-yellow-50"
				mainAxisAlignment="center"
				crossAxisAlignment="center"
			>
				<TextComponent
					variant="h5"
					className="text-yellow-600 font-bold"
				>
					Crea el sorteo públicamente para programarlo para la fecha que desees y poder compartir el resultado con los participantes
				</TextComponent>
				<Ref
					className="font-bold w-full"
					color="yellow"
					href="/createlottery"
					secondary
				>
					<Row
						className="gap-1"
						crossAxisAlignment="center"
						mainAxisAlignment="center"
					>
						<LinkIcon className="w-5 h-5" />
						<span>Crea el sorteo públicamente</span>
					</Row>
				</Ref>
			</Column>
		</Column>
	);
};
